// Shared app-shell: ONE collapsible left column — logo + version on top (click →
// home), vertical nav below it, and the signed-in user pinned to the bottom —
// next to a single scrolling content region. The full-width header this dashboard
// used to wear is gone: brand and userbar were the only things in it, and both
// belong to the column that already carries the navigation (the workspace shell
// has read this way for a while; the dashboard now matches).
//
// The version rides in the LOGO ROW, small and muted next to the product name —
// it is an identity fact, so it belongs where the identity is. The old sidebar
// footer that paired it with an online/offline dot is gone entirely: the dot said
// nothing a failing request does not say louder, and it cost a whole row.
//
// The collapse toggle shrinks the column to an icons-only rail; the right edge of
// the column is a drag handle that resizes it between SIDEBAR_MIN_W and
// SIDEBAR_MAX_W (double-click → back to the default). Both choices are statements
// about this browser, so both are remembered in localStorage and survive a
// reload — under separate keys, and collapse wins while it is on (expanding
// restores the width that was there before). Admin-only nav items are gated.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { NavLink, Link, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Avatar } from './ui';
import { DeviceLinkDialog } from './DeviceLinkDialog';
// The route only — not the Nodes view's button component, which would drag a whole page's worth of
// imports into the shell. Both places point at the same constant, so they cannot drift apart.
import { DOWNLOAD_ROUTE as NODE_CLIENT_PATH } from '../views/Download';
import logoUrl from '../assets/logo.svg';

// App version shown next to the product name in the logo row (H21). Single source
// of truth: `window.__MCPGRAPH__.APP_VERSION` when the embedding host injects it
// (the GUI embed sets it in H26), otherwise the build-time package.json version
// baked in as __APP_VERSION__ (see vite.config.ts) — what the standalone build
// shows.
const VERSION =
  (typeof window !== 'undefined' && window.__MCPGRAPH__?.APP_VERSION) || __APP_VERSION__;

export interface NavItem {
  to: string;
  label: string;
  admin?: boolean;
  /** 24×24 stroke path — the icon shown in the collapsed rail (and beside the label). */
  icon: string;
}

// Nav order and role gating (UX10/UX24).
//
// The order is the one the user asked for, which is the order the work happens in:
// what you watch (Overview), what you configure (Networks, Nodes, Services,
// Streams), what you produce (Projects, Storage Browser), how you connect (MCP), and
// what you read back afterwards (Audit, Logs). Assistant and Users were not named;
// they go after the named rows because both are ADMIN surfaces — org-wide
// configuration and the org directory — and neither is part of anyone's daily loop.
//
// PROFILE IS NOT HERE (UX24). The page still exists at /profile and is still
// reachable — from the userbar menu at the bottom of this very column, which already
// carries the account, its email and Sign out. A permanent row for a page you open
// twice a year, in the same column as a menu that already links it, was the nav
// carrying one thing twice.
//
// Role gating: what stays `admin: true` is what has no per-user answer to give —
// Users (the org directory) and Assistant (the org-wide assistant config). Everything
// else reads endpoints the gateway scopes BY IDENTITY, so a member sees their own
// rows; this list never narrows a payload, the server does.
//
// Logs (ex-Errors) LOST its admin flag: it aggregates failed tasks, errored streams
// and offline nodes — all caller-scoped — plus the audit feed and the call log that
// every member could already read on Overview. Its one process-wide block, the
// metrics threshold alerts, is gated inside the page.
//
// Five rows are GONE, each folded into the page it was always about: Tokens split
// (UX15/UX16) — the mcp-access half into MCP, the client-token half into Nodes —
// Engines into Assistant (UX17), Tasks back into Overview (UX19), and Profile into
// the userbar menu (UX24). Their URLs are redirects in App.tsx (Profile keeps its
// own route), never nav rows: a row pointing at a redirect is how a nav grows two
// names for one page.
// BF29 — the Performance tab is gone (its signals live in Overview / Logs).
export const NAV: NavItem[] = [
  { to: '/', label: 'Overview', icon: 'M4 13h6V4H4zM14 20h6v-9h-6zM4 20h6v-4H4zM14 8h6V4h-6z' },
  { to: '/networks', label: 'Networks', icon: 'M12 4v5M6 20v-4M18 20v-4M6 16h12M12 9v7M12 9a2 2 0 100-4 2 2 0 000 4' },
  { to: '/clients', label: 'Nodes', icon: 'M4 5h16v5H4zM4 14h16v5H4zM7 7.5h.01M7 16.5h.01' },
  { to: '/services', label: 'Services', icon: 'M4 7h16M4 12h16M4 17h10' },
  // UX11 — "Streams", drawn as what a stream is: a wave that keeps going.
  { to: '/streams', label: 'Streams', icon: 'M3 8c2.2-3 4.4-3 6.6 0s4.4 3 6.6 0 4.4-3 4.8-1.5M3 16c2.2-3 4.4-3 6.6 0s4.4 3 6.6 0 4.4-3 4.8-1.5' },
  { to: '/projects', label: 'Projects', icon: 'M4 7a2 2 0 012-2h3l2 2h7a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2z' },
  { to: '/objects', label: 'Storage Browser', icon: 'M4 7c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3zM4 7v10c0 1.7 3.6 3 8 3s8-1.3 8-3V7' },
  // UX14 — "MCP": the whole subject, not one table of it. The page carries the
  // mcp-access tokens you connect with AND the sessions they open (UX15).
  { to: '/sessions', label: 'MCP', icon: 'M4 6h16v11H4zM9 20h6M12 17v3' },
  { to: '/audit', label: 'Audit', icon: 'M12 3l7 3v5c0 4.2-2.8 7.7-7 9-4.2-1.3-7-4.8-7-9V6zM9.5 11.5l1.8 1.8 3.5-3.5' },
  // UX12 — "Logs", drawn as a journal: a bound book with written lines.
  { to: '/logs', label: 'Logs', icon: 'M5 4h11a2 2 0 012 2v14H7a2 2 0 01-2-2zM5 18a2 2 0 012-2h11M9 8h6M9 11h6' },
  { to: '/assistant', label: 'Assistant', admin: true, icon: 'M5 5h14v10H9l-4 4z' },
  { to: '/users', label: 'Users', admin: true, icon: 'M9 11a3 3 0 100-6 3 3 0 000 6zM3 19c0-2.8 2.7-5 6-5s6 2.2 6 5M16 6.5a3 3 0 010 5.8M18 19c0-2-.7-3.4-1.8-4.4' },
];// Nav items the current role may see. Pure (exported for tests): a non-admin gets
// every identity-scoped surface and none of the four org-wide operator ones.
export function visibleNav(isAdmin: boolean): NavItem[] {
  return NAV.filter((n) => !n.admin || isAdmin);
}

// --- Sidebar collapse state (persisted per browser) --------------------------

export const COLLAPSE_KEY = 'mcpgraph.dashboard.sidebar-collapsed';

// Read the remembered collapse state. Anything other than the literal '1' — no
// entry, a stale value, a browser that throws on storage (private mode, blocked
// site data) — means "expanded", the safe default. Pure-ish, exported for tests.
export function readCollapsed(store?: Pick<Storage, 'getItem'>): boolean {
  try {
    const s = store ?? (typeof localStorage === 'undefined' ? null : localStorage);
    return s?.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeCollapsed(next: boolean, store?: Pick<Storage, 'setItem'>): void {
  try {
    const s = store ?? (typeof localStorage === 'undefined' ? null : localStorage);
    s?.setItem(COLLAPSE_KEY, next ? '1' : '0');
  } catch {
    /* storage unavailable — the choice just does not survive the reload */
  }
}

// --- Sidebar width (drag-resized, persisted per browser) ---------------------
//
// A SEPARATE key from the collapse state, because they answer different
// questions: collapse is "is the column a rail right now", width is "how wide is
// it when it is not". Keeping them apart is what lets expanding restore the width
// the user had chosen. The same key/limits are mirrored in the node dashboard's
// src/shell.ts — the two shells resize identically.

export const WIDTH_KEY = 'mcpgraph.dashboard.sidebar-width';
export const SIDEBAR_MIN_W = 180;
export const SIDEBAR_MAX_W = 420;
// 280, not the 240 the CSS falls back to: the logo row carries the version now, and
// at 240 "mcpgraph · gateway" ellipsised to "mcpgraph · ga…" out of the box. A
// default that truncates the product name is the wrong default. Measured in the
// browser: the name needs 129px of the row and only clears at 270, so 280 leaves a
// margin for a system font that renders wider than this one.
export const SIDEBAR_DEFAULT_W = 280;

/** Clamp to the allowed range; anything not a finite number falls back to the default. */
export function clampWidth(px: number): number {
  if (!Number.isFinite(px)) return SIDEBAR_DEFAULT_W;
  return Math.min(SIDEBAR_MAX_W, Math.max(SIDEBAR_MIN_W, Math.round(px)));
}

/**
 * Read the remembered width. A missing entry, junk, or a storage that throws all
 * mean "the default" — and a stored value from an older, wider build is clamped
 * rather than honoured, so the column can never come back off-screen.
 */
export function readWidth(store?: Pick<Storage, 'getItem'>): number {
  try {
    const s = store ?? (typeof localStorage === 'undefined' ? null : localStorage);
    const raw = s?.getItem(WIDTH_KEY);
    if (raw == null || raw === '') return SIDEBAR_DEFAULT_W;
    return clampWidth(Number(raw));
  } catch {
    return SIDEBAR_DEFAULT_W;
  }
}

export function writeWidth(px: number, store?: Pick<Storage, 'setItem'>): void {
  try {
    const s = store ?? (typeof localStorage === 'undefined' ? null : localStorage);
    s?.setItem(WIDTH_KEY, String(clampWidth(px)));
  } catch {
    /* storage unavailable — the choice just does not survive the reload */
  }
}

function Icon({ d, className = 'nav-icon' }: { d: string; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

export function Layout() {
  const { user, isAdmin, logout } = useAuth();
  // AH4 — the "sign in on another device" QR dialog.
  const [deviceLinkOpen, setDeviceLinkOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [width, setWidth] = useState(readWidth);
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const userbarRef = useRef<HTMLDivElement | null>(null);

  function toggleCollapsed() {
    setCollapsed((c) => {
      writeCollapsed(!c);
      return !c;
    });
    setMenuOpen(false);
  }

  // --- Resize drag ----------------------------------------------------------
  //
  // The move/up listeners go on the DOCUMENT rather than on the handle with
  // setPointerCapture: the pointer spends the whole drag outside the 6px handle,
  // and jsdom has no pointer capture at all — capture would make the gesture
  // untestable and silently drop frames over the content region in a real browser
  // too (iframes, canvases). Touch comes along for free: these are pointer events
  // and the handle sets `touch-action: none`.
  function startResize(clientX: number) {
    if (collapsed) return;
    dragRef.current = { startX: clientX, startW: width };
    setResizing(true);
  }

  useEffect(() => {
    if (!resizing) return;
    function onMove(e: PointerEvent) {
      const d = dragRef.current;
      if (!d) return;
      setWidth(clampWidth(d.startW + (e.clientX - d.startX)));
    }
    function onUp() {
      dragRef.current = null;
      setResizing(false);
    }
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [resizing]);

  // Persist once the gesture settles — not on every mouse move.
  useEffect(() => {
    if (!resizing) writeWidth(width);
  }, [resizing, width]);

  function resetWidth() {
    dragRef.current = null;
    setResizing(false);
    setWidth(SIDEBAR_DEFAULT_W);
  }

  // Dismiss the account menu on an outside click or Escape — it is a popover over
  // the whole app, so it must not outlive the user's attention.
  useEffect(() => {
    if (!menuOpen) return;
    function onDocDown(e: MouseEvent) {
      if (!userbarRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const displayName = user?.name || user?.email || 'Account';

  return (
    <div
      className={`app-shell${collapsed ? ' collapsed' : ''}${resizing ? ' resizing' : ''}`}
      data-testid="app-shell"
      style={{ '--sidebar-w': `${width}px` } as CSSProperties}
    >
      <aside className="app-sidebar">
        <div className="sidebar-brand-row">
          {/* The logo is the way home: clicking it goes to the dashboard root.
              The version rides right here, next to the name — small and muted;
              in the collapsed rail there is no room for either. */}
          <Link to="/" className="sidebar-brand" title={`mcpgraph · gateway v${VERSION}`}>
            <img className="app-logo" src={logoUrl} alt="mcpgraph" />
            {!collapsed && <span className="app-title">mcpgraph · gateway</span>}
            {!collapsed && (
              <span className="app-version" data-testid="app-version">
                v{VERSION}
              </span>
            )}
          </Link>
          <button
            className="sidebar-toggle"
            data-testid="sidebar-toggle"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={toggleCollapsed}
          >
            <Icon className="nav-icon" d={collapsed ? 'M9 6l6 6-6 6' : 'M15 6l-6 6 6 6'} />
          </button>
        </div>

        <nav className="app-nav">
          {visibleNav(isAdmin).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              title={n.label}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            >
              <Icon d={n.icon} />
              {!collapsed && <span className="nav-label">{n.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* The node client, at the foot of the column. Not a nav row: it is an ACTION, so it is
            drawn as a button — its own border, centred in the line, sized to its label rather than
            stretched across the column — and it stays out of `NAV`. It is the one thing a person
            needs from any page and not only from Nodes: the installer that turns the machine they
            are sitting at into a node of this gateway. Collapsed, it is the icon alone.
            The rule below it separates it from the account, which is the next thing down. */}
        <Link
          className="btn sidebar-download"
          to={NODE_CLIENT_PATH}
          title="Download node client"
          data-testid="sidebar-download-node-client"
        >
          <Icon className="nav-icon" d="M12 4v10m0 0l-3.5-3.5M12 14l3.5-3.5M5 18h14" />
          {!collapsed && <span>Download node client</span>}
        </Link>
        <div className="sidebar-sep" role="separator" data-testid="sidebar-userbar-separator" />

        {/* Userbar — the account, pinned to the bottom of the column. */}
        <div className="app-userbar" ref={userbarRef}>
          <button
            className="userbar-trigger"
            data-testid="userbar-trigger"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={displayName}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <Avatar name={displayName} colorKey={user?.id || user?.name || user?.email} />
            {!collapsed && <span className="userbar-name">{displayName}</span>}
            {!collapsed && isAdmin && <span className="chip">admin</span>}
            <Icon className="nav-icon userbar-caret" d="M6 15l6-6 6 6" />
          </button>

          {menuOpen && (
            <div className="userbar-menu" role="menu" data-testid="userbar-menu">
              <div className="userbar-menu-head">
                <div className="userbar-menu-name">{displayName}</div>
                {user?.email && <div className="muted small">{user.email}</div>}
              </div>
              <Link className="userbar-item" role="menuitem" to="/profile" onClick={() => setMenuOpen(false)}>
                Profile
              </Link>
              {/* AH4 — "sign in on another device": this device mints a one-time login link and
                  draws it as a QR the other device scans. It sits next to Sign out because both
                  are statements about WHERE this account is signed in. */}
              <button
                className="userbar-item"
                role="menuitem"
                data-testid="device-link-open"
                onClick={() => {
                  setMenuOpen(false);
                  setDeviceLinkOpen(true);
                }}
              >
                Sign in on another device
              </button>
              <button
                className="userbar-item"
                role="menuitem"
                data-testid="sign-out"
                onClick={() => {
                  setMenuOpen(false);
                  void logout();
                }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>

        {/* The right edge is the resize handle: drag to set the width, double-click
            to go back to the default. It is a real focusable separator, so the
            column can be resized from the keyboard too. Hidden while collapsed —
            the rail has one width. */}
        {!collapsed && (
          <div
            className="sidebar-resizer"
            data-testid="sidebar-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
            aria-valuenow={width}
            aria-valuemin={SIDEBAR_MIN_W}
            aria-valuemax={SIDEBAR_MAX_W}
            tabIndex={0}
            onPointerDown={(e) => {
              e.preventDefault();
              startResize(e.clientX);
            }}
            onDoubleClick={resetWidth}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') setWidth((w) => clampWidth(w - 16));
              else if (e.key === 'ArrowRight') setWidth((w) => clampWidth(w + 16));
              else if (e.key === 'Home') resetWidth();
              else return;
              e.preventDefault();
            }}
          />
        )}
      </aside>

      <main className="app-content">
        <Outlet />
      </main>

      {deviceLinkOpen && <DeviceLinkDialog onClose={() => setDeviceLinkOpen(false)} />}
    </div>
  );
}
